import { useMemo } from "react";
import { useNavigate } from "react-router";
import { Dropdown } from "@uhg-harmony/react";
import type { MastheadProps } from "@uhg-harmony/react";
import type { AuthContextProps } from "react-oidc-context";
import { isReadOnlyUser } from "../../redux/user/roleHelpers";
import type { UserRole } from "../../redux/user/userTypes";
import type { AccountOption, ClientOption } from "../../types/optionTypes";
import { createLogger } from "../../utils/logger";
import { TemplatesDataConnector } from "../../api/templatesDataConnector";
import {
  getSupportUploadMenuItem,
  isSupportUploadEnabled,
} from "../../../support";
import {
  SELECT_ACCOUNT_PLACEHOLDER,
  SELECT_CLIENT_PLACEHOLDER,
  getInitials,
} from "./appLayoutHelpers";

const logger = createLogger("AppLayout");

export function useMastheadProps({
  usernameFromAuth,
  emailFromAuth,
  auth,
  token,
  env,
  onSupportUploadClick,
  onGetImageMetadataClick,
  notificationMenuValues,
  accounts,
  selectedAccount,
  setSelectedAccount,
  clients,
  selectedClients,
  setSelectedClients,
  userRole,
  clientsLoading,
  treeLoading,
}: {
  usernameFromAuth: string;
  emailFromAuth: string;
  auth: AuthContextProps;
  token: string;
  env: string;
  onSupportUploadClick: () => void;
  onGetImageMetadataClick: () => void;
  notificationMenuValues: MastheadProps["notificationMenuValues"];
  accounts: AccountOption[];
  selectedAccount: AccountOption | null;
  setSelectedAccount: (value: AccountOption) => void;
  clients: ClientOption[];
  selectedClients: ClientOption | null;
  setSelectedClients: (value: ClientOption) => void;
  userRole: UserRole;
  clientsLoading: boolean;
  treeLoading: boolean;
}) {
  const navigate = useNavigate();
  const hasSingleAccountOption = accounts.length === 2;
  const singleAccountValue = hasSingleAccountOption ? accounts[1]?.value : "";
  const isSingleAccountDefaultSelected =
    hasSingleAccountOption &&
    !!singleAccountValue &&
    selectedAccount?.value === singleAccountValue;
  const supportUploadEnabled = isSupportUploadEnabled(env);

  return useMemo(
    () => ({
      productSwitcherValues: {
        currentProductId: "1",
        products: [
          {
            clickHandler: () => {},
            id: "1",
            name: "Template Studio",
          },
        ],
      },
      clientSwitcherValues: {
        ClientSwitcherOverride: () => (
          <div className="masthead-dropdowns">
            <div className="dropdown-item">
              <span className="dropdown-label">Account:</span>
              <Dropdown
                className="borderless-dropdown"
                type="single"
                items={
                  userRole === "TB_ECP_ADMIN"
                    ? accounts
                    : accounts.filter(
                        (option) => option !== SELECT_ACCOUNT_PLACEHOLDER,
                      )
                }
                value={selectedAccount}
                onChange={setSelectedAccount}
                showClearButton={false}
                placeholderText="Select Account"
                disabled={
                  isReadOnlyUser(userRole) ||
                  clientsLoading ||
                  treeLoading ||
                  (isSingleAccountDefaultSelected &&
                    userRole !== "TB_ECP_ADMIN")
                }
              />
            </div>

            <div className="dropdown-item">
              <span className="dropdown-label">Client:</span>
              <Dropdown
                className="borderless-dropdown"
                type="single"
                items={
                  userRole === "TB_ECP_ADMIN"
                    ? clients
                    : clients.filter(
                        (option) => option !== SELECT_CLIENT_PLACEHOLDER,
                      )
                }
                value={selectedClients}
                onChange={setSelectedClients}
                showClearButton={false}
                placeholderText="Select Client"
                disabled={
                  isReadOnlyUser(userRole) ||
                  clientsLoading ||
                  treeLoading ||
                  clients.length <= 1 ||
                  !selectedAccount?.value
                }
              />
            </div>
          </div>
        ),
      },
      userMenuValues: {
        displayName: usernameFromAuth,
        initials: getInitials(usernameFromAuth),
        email: emailFromAuth,
        menuItems: [
          ...(supportUploadEnabled
            ? [getSupportUploadMenuItem(onSupportUploadClick)]
            : []),
          {
            id: "getImageMetaData",
            label: "getImageMetaData",
            clickHandler: onGetImageMetadataClick,
          },

          ...(window.location.hostname.includes("local") ||
          window.location.hostname.includes("dev")
            ? [
                {
                  id: "profile",
                  label: "Copy User Token",
                  clickHandler: () => {
                    navigator.clipboard.writeText(token);
                  },
                },
              ]
            : []),
        ],
        onSignOut: () => {
          TemplatesDataConnector.clearCaches();
          auth
            .signoutRedirect({
              post_logout_redirect_uri: `${window.location.origin}/logout`,
            })
            .catch((err: unknown) => logger.error("Logout failed:", err));
        },
      },
      notificationMenuValues,
    }),
    [
      auth,
      emailFromAuth,
      notificationMenuValues,
      usernameFromAuth,
      navigate,
      accounts,
      selectedAccount,
      setSelectedAccount,
      clients,
      selectedClients,
      setSelectedClients,
      userRole,
      clientsLoading,
      treeLoading,
      isSingleAccountDefaultSelected,
      onSupportUploadClick,
      onGetImageMetadataClick,
      supportUploadEnabled,
    ],
  );
}
